import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from './ui/input';
import { StandardButton } from '../styles/StandardButtons';
import { AuthBanner } from './AuthBanner';
import { supabase } from '../lib/supabase';
import { waitForInitialSession } from '../lib/auth-session';
import { getApiUrl } from '../lib/api-config';
import './Setup.css';

export const MIN_INVITE_PASSWORD_LENGTH = 8;

// The email link carries a one-time token hash (?token_hash=...&type=invite|recovery). It is verified here with verifyOtp -- not by Auth's own /verify address, whose
// answer (the session in the URL #fragment) the app's PKCE client refuses and which mail scanners consume before the person clicks. A token can be spent only once,
// and React (StrictMode in development, a remount) may run the effect twice: both runs share ONE verification per token, so the second never fails on a spent token.
const OTP_TYPES = ['invite', 'recovery'] as const;
type OtpType = (typeof OTP_TYPES)[number];
const verifications = new Map<string, Promise<boolean>>();

function verifyInviteToken(tokenHash: string, type: OtpType): Promise<boolean> {
  const key = `${type}:${tokenHash}`;
  let pending = verifications.get(key);
  if (!pending) {
    pending = supabase.auth.verifyOtp({ token_hash: tokenHash, type })
      .then(({ data, error }) => !error && !!data.session)
      .catch(() => false);
    verifications.set(key, pending);
  }
  return pending;
}

/** The token in the address, if any. Returns null for a page opened without one (the session may already exist). */
function tokenFromLocation(): { tokenHash: string; type: OtpType } | null {
  const params = new URLSearchParams(window.location.search);
  const tokenHash = params.get('token_hash');
  const type = params.get('type') as OtpType | null;
  if (!tokenHash) return null;
  return { tokenHash, type: type && OTP_TYPES.includes(type) ? type : 'invite' };
}

/**
 * Where the link in a KrewUp invitation email lands (<app address>/accept-invite). Supabase Auth has already verified the one-time link and signed the invited
 * person in (the session arrives in the URL and is read by the shared session cache); this page lets them choose their password, then tells the backend the
 * invitation was accepted. The company and the role were attached when the admin invited them, so after this they simply enter the app.
 * Without a session (the link was already used, or it expired) it says so and sends them to their admin: no password is ever accepted without a valid link.
 */
export function AcceptInvitePage() {
  const [phase, setPhase] = useState<'checking' | 'no-session' | 'form' | 'done'>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = tokenFromLocation();
      if (token) {
        const verified = await verifyInviteToken(token.tokenHash, token.type);
        // the token is spent: take it out of the address bar and the history
        if (verified) window.history.replaceState(null, '', window.location.pathname);
        if (!cancelled) setPhase(verified ? 'form' : 'no-session');
        return;
      }
      const session = await waitForInitialSession();
      if (!cancelled) setPhase(session ? 'form' : 'no-session');
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setError(null);
    if (password.length < MIN_INVITE_PASSWORD_LENGTH) { setError(`Your password must have at least ${MIN_INVITE_PASSWORD_LENGTH} characters.`); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }
    setSaving(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      // close the invitation; the password is already set, so a failure here is told but does not lock the person out
      try {
        const response = await fetch(getApiUrl('api/people/invitations/accept'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
        if (!response.ok) throw new Error('not accepted');
      } catch {
        setNotice('Your password is set. We could not mark the invitation as accepted yet; this does not affect your access.');
      }
      setPhase('done');
    } catch (failure) {
      console.error('Error accepting the invitation:', failure);
      setError(failure instanceof Error ? failure.message : 'Your password could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="setup-container">
      <div className="setup-content">
        <AuthBanner />
        <h1 className="h1">Accept your invitation</h1>

        {phase === 'checking' && <p className="text-sm text-gray-500">Checking your invitation…</p>}

        {phase === 'no-session' && (
          <div className="space-y-3" data-testid="invite-invalid">
            <div className="error-message" role="alert">This invitation link is no longer valid. It may have expired or already been used. Ask your administrator to send you a new invitation.</div>
            <Link to="/login" className="text-sm underline">Go to login</Link>
          </div>
        )}

        {phase === 'form' && (
          <form onSubmit={submit}>
            <p className="text-sm text-gray-600 mb-4">Choose a password to finish setting up your KrewUp account.</p>
            {error && <div className="error-message" role="alert">{error}</div>}
            <div className="form-group">
              <label htmlFor="invite-password" className="form-label">Password</label>
              <Input id="invite-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="invite-password-confirm" className="form-label">Confirm password</label>
              <Input id="invite-password-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            <StandardButton customVariant="blue" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Set password'}</StandardButton>
          </form>
        )}

        {phase === 'done' && (
          <div className="space-y-3" data-testid="invite-done">
            <p className="text-sm text-gray-700">You're all set.</p>
            {notice && <p className="text-sm text-amber-700" role="status">{notice}</p>}
            <StandardButton customVariant="blue" type="button" onClick={() => { window.location.href = '/'; }}>Open KrewUp</StandardButton>
          </div>
        )}
      </div>
    </div>
  );
}
